import React, { useMemo } from "react";
import LazyLoad from "../../image/LazyLoad";
import { formatImageUrl, formatReviewScore } from "@/lib/general";
import { TripLodging, Location } from "@/types";
import { Search, Bed } from "lucide-react";
import { HomeIcon } from "@heroicons/react/24/solid";
import { StarIcon } from "@heroicons/react/24/outline";
import { formatReviewCount } from "@/lib/general";

interface AccommodationProps {
  onSearchClick: () => void;
  mainLocationLodging: TripLodging | null;
  selectedLocation: Location | null;
}

const Accommodation: React.FC<AccommodationProps> = ({
  onSearchClick,
  mainLocationLodging,
  selectedLocation,
}) => {
  const locationNights = useMemo(() => {
    if (!selectedLocation?.date) return 0;
    const startDate = new Date(selectedLocation.date.start_date);
    const endDate = new Date(selectedLocation.date.end_date);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }, [selectedLocation]);

  const RatingInfo = ({ score, count }: { score: number; count: number }) => (
    <div className="flex items-center space-x-1 2xl:space-x-1.5 2xl:mt-1">
      <StarIcon className="w-3.5 h-3.5 2xl:w-5 2xl:h-5 text-white/90 fill-white/90" />
      <span className="2xl:text-[15px]">
        {formatReviewScore(score)} ({formatReviewCount(count)} reviews)
      </span>
    </div>
  );

  const renderLodgingInfo = () => {
    if (!mainLocationLodging) return null;

    const InfoDot = () => (
      <div className="w-1 h-1 2xl:w-1.5 2xl:h-1.5 rounded-full bg-white translate-y-[0.5px]"></div>
    );

    const InfoText = ({ children }: { children: React.ReactNode }) => (
      <h1 className="text-white/90 font-light text-[13px] 2xl:text-[15px]">
        {children}
      </h1>
    );

    if (mainLocationLodging.type === "hotel") {
      return (
        <div className="flex flex-row items-center space-x-2">
          {mainLocationLodging.moreInfo?.roomName ? (
            <>
              <InfoText>{mainLocationLodging.moreInfo.roomName}</InfoText>
              <InfoDot />
            </>
          ) : (
            mainLocationLodging.moreInfo?.reviewScore && (
              <>
                <InfoText>
                  <RatingInfo
                    score={mainLocationLodging.moreInfo.reviewScore}
                    count={mainLocationLodging.moreInfo.reviewCount || 0}
                  />
                </InfoText>
                <InfoDot />
              </>
            )
          )}
          <InfoText>{locationNights} nights</InfoText>
        </div>
      );
    }

    return (
      <div className="flex flex-row items-center space-x-2">
        {mainLocationLodging.moreInfo?.propertyType && (
          <>
            <InfoText>
              {mainLocationLodging.moreInfo.propertyType}
              {mainLocationLodging.moreInfo.neighborhood && (
                <> in {mainLocationLodging.moreInfo.neighborhood}</>
              )}
            </InfoText>
            <InfoDot />
          </>
        )}
        <InfoText>{locationNights} nights</InfoText>
      </div>
    );
  };

  const renderNoLodgingsContent = () => (
    <div className="w-full h-full bg-neutral-50 2xl:bg-neutral-100 rounded-xl flex flex-col p-2 2xl:p-2.5 border-[1px] border-neutral-100 ring-1 ring-neutral-100 shadow">
      <div className="flex flex-row justify-between w-full items-center mb-2.5 2xl:mb-3 px-1 2xl:px-1.5">
        <div className="flex flex-row items-center pr-3 2xl:pr-4 space-x-2 ring-1 ring-neutral-200 rounded-full h-8 2xl:h-9">
          <div className="flex items-center justify-center w-8 h-8 2xl:w-9 2xl:h-9 bg-indigo-800 rounded-full">
            <HomeIcon className="w-4 h-4 2xl:w-[18px] 2xl:h-[18px] text-white" />
          </div>
          <p className="text-sm 2xl:text-[17px] font-light text-neutral-700">
            Accommodation
          </p>
        </div>
      </div>

      <div className="min-h-[90%] rounded-lg max-h-[90%] overflow-hidden relative">
        <div className="h-full rounded-lg flex flex-col items-center justify-center space-y-4">
          <HomeIcon className="w-6 h-6 2xl:w-7 2xl:h-7 text-neutral-400" />
          <p className="text-[13.5px] 2xl:text-[15px] text-neutral-400 font-light">
            No accommodation added
          </p>
          <button
            onClick={onSearchClick}
            className="inline-flex items-center space-x-1.5 text-white hover:bg-indigo-800 bg-indigo-700 rounded-full px-3 2xl:px-4 py-2 text-[12px] 2xl:text-[13px] font-medium transition-colors duration-150"
          >
            <Search className="w-4 h-4 2xl:w-[18px] 2xl:h-[18px]" />
            <span>Search Stays</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="w-full bg-neutral-100 flex-1 h-full flex flex-col justify-center items-center rounded-xl 2xl:rounded-2xl group"
      data-tutorial="overview-accommodation"
    >
      {!mainLocationLodging ? (
        renderNoLodgingsContent()
      ) : (
        <div className="relative w-full h-full overflow-hidden rounded-lg 2xl:rounded-xl">
          <LazyLoad
            src={formatImageUrl(mainLocationLodging.mainImageURL, "original")}
            lowResSrc={formatImageUrl(mainLocationLodging.mainImageURL, "low")}
            alt={mainLocationLodging.name}
            className="w-full h-full"
            objectFit="cover"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black to-transparent pt-12 2xl:pt-14 pb-3 2xl:pb-4">
            <div className="px-3 2xl:px-4 pb-1 2xl:pb-1.5">
              <h1 className="text-white font-medium leading-relaxed text-md 2xl:text-2xl truncate w-[90%]">
                {mainLocationLodging.name}
              </h1>
              {renderLodgingInfo()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accommodation;
